import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {ExpandMore, BugReport} from '@mui/icons-material';
import {DefectCard} from './DefectCard';

type DefectSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type DefectStatus =
  | 'IDENTIFIED'
  | 'IN_REVIEW'
  | 'ACCEPTED'
  | 'RESOLVED'
  | 'REJECTED';

interface Defect {
  id: string;
  title: string;
  description?: string | null;
  severity: DefectSeverity;
  defectStatus: DefectStatus;
  roomType?: string | null;
  defectCategory?: string | null;
  sourceClipIndex?: number | null;
  sourceFramePosition?: string | null;
  aiDetected: boolean;
  aiConfidence?: number | null;
  evidenceImageURI?: string | null;
  audioStartSeconds?: number | null;
  audioEndSeconds?: number | null;
}

interface DefectListProps {
  defects: Defect[];
  onEditDefect?: (defect: Defect) => void;
  onDeleteDefect?: (defectId: string) => void;
  onPlayAudio?: (defect: Defect) => void;
  groupByRoom?: boolean;
}

export function DefectList({
  defects,
  onEditDefect,
  onDeleteDefect,
  onPlayAudio,
  groupByRoom = true,
}: DefectListProps) {
  if (defects.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <BugReport sx={{fontSize: 60, color: 'text.secondary', mb: 2}} />
        <Typography variant="h6" gutterBottom>
          No defects found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add defects manually or use AI analysis
        </Typography>
      </Box>
    );
  }

  if (!groupByRoom) {
    return (
      <Stack spacing={1}>
        {defects.map((defect) => (
          <DefectCard
            key={defect.id}
            defect={defect}
            onEdit={onEditDefect ? () => onEditDefect(defect) : undefined}
            onDelete={
              onDeleteDefect ? () => onDeleteDefect(defect.id) : undefined
            }
            onPlayAudio={onPlayAudio ? () => onPlayAudio(defect) : undefined}
          />
        ))}
      </Stack>
    );
  }

  // Group defects by room type
  const groupedDefects = defects.reduce(
    (acc: Record<string, Defect[]>, defect) => {
      const room = defect.roomType || 'General';
      if (!acc[room]) {
        acc[room] = [];
      }
      acc[room].push(defect);
      return acc;
    },
    {}
  );

  const getSeverityCounts = (roomDefects: Defect[]) => {
    return {
      critical: roomDefects.filter((d) => d.severity === 'CRITICAL').length,
      high: roomDefects.filter((d) => d.severity === 'HIGH').length,
      medium: roomDefects.filter((d) => d.severity === 'MEDIUM').length,
      low: roomDefects.filter((d) => d.severity === 'LOW').length,
    };
  };

  return (
    <Box>
      {Object.entries(groupedDefects).map(([roomType, roomDefects]) => {
        const counts = getSeverityCounts(roomDefects);
        return (
          <Accordion key={roomType} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                mr={2}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {roomType}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {counts.critical > 0 && (
                    <Chip
                      label={counts.critical}
                      size="small"
                      sx={{
                        bgcolor: '#dc3545',
                        color: '#fff',
                        height: 20,
                        minWidth: 24,
                      }}
                    />
                  )}
                  {counts.high > 0 && (
                    <Chip
                      label={counts.high}
                      size="small"
                      sx={{
                        bgcolor: '#ff9800',
                        color: '#fff',
                        height: 20,
                        minWidth: 24,
                      }}
                    />
                  )}
                  {counts.medium > 0 && (
                    <Chip
                      label={counts.medium}
                      size="small"
                      sx={{
                        bgcolor: '#ffc107',
                        color: '#000',
                        height: 20,
                        minWidth: 24,
                      }}
                    />
                  )}
                  {counts.low > 0 && (
                    <Chip
                      label={counts.low}
                      size="small"
                      sx={{
                        bgcolor: '#4caf50',
                        color: '#fff',
                        height: 20,
                        minWidth: 24,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {roomDefects.map((defect) => (
                  <DefectCard
                    key={defect.id}
                    defect={defect}
                    onEdit={
                      onEditDefect ? () => onEditDefect(defect) : undefined
                    }
                    onDelete={
                      onDeleteDefect
                        ? () => onDeleteDefect(defect.id)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
